import { Token } from '@pancakeswap/sdk'
import {
  Box,
  Flex,
  Heading,
  IconButton,
  InjectedModalProps,
  LinkExternal,
  ModalContainer,
  ModalHeader,
  Skeleton,
  Text,
  useMatchBreakpoints,
} from '@pancakeswap/uikit'
import truncateHash from '@pancakeswap/utils/truncateHash'
import useTheme from 'hooks/useTheme'
import { useProfileForAddress } from 'state/profile/hooks'
import styled from 'styled-components'
import { getBlockExploreLink } from 'utils'

import { useTranslation } from '@pancakeswap/localization'
import { FetchStatus } from 'config/constants/types'
import { PredictionUser } from 'state/types'
import InfoTooltip from '@pancakeswap/uikit/src/components/Timeline/InfoTooltip'
import MobileBetsTable from './MobileBetsTable'
import DesktopBetsTable from './Results/DesktopBetsTable'
import { NetWinningsView } from './Results/styles'

interface WalletStatsModalProps extends InjectedModalProps {
  account?: string
  onBeforeDismiss?: () => void
  address: string
  result: PredictionUser
  leaderboardLoadingState: FetchStatus
  token: Token
  api: string
}

const StyledFlex = styled(Flex)`
  > div {
    max-width: calc((100% - 72px) / 3);
  }

  @media (max-width: 767px) {
    /* flex-direction: column; */
    display: grid;
    grid-template-columns: 1fr;

    padding: 16px;
    gap: 12px;
    > div {
      max-width: unset;
      display: flex;
      justify-content: space-between;
      align-items: center;
      h6 {
        margin-bottom: 0;
      }
      div:not(.tooltip) {
        align-items: flex-end;
      }
    }
  }
`

const ExternalLink = styled(LinkExternal)`
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  font-weight: 700;
  svg path {
    fill: rgba(255, 255, 255, 0.6);
  }
  &:hover {
    color: #03f5ae;
    svg path {
      fill: #03f5ae;
    }
  }
`

const StyledIconButton = styled(IconButton)`
  background: transparent;
  width: 16px;
`

const StyledModalContainer = styled(ModalContainer)`
  width: 460px;
  background: #20233b;
  @media (max-width: 767px) {
    width: calc(100% - 32px);
  }
`

const WalletStatsModal: React.FC<React.PropsWithChildren<WalletStatsModalProps>> = ({
  result,
  address,
  leaderboardLoadingState,
  onDismiss,
  onBeforeDismiss,
  token,
  api,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { profile } = useProfileForAddress(address)
  const isLoading = leaderboardLoadingState === FetchStatus.Fetching
  const { isDesktop } = useMatchBreakpoints()

  const handleDismiss = () => {
    if (onBeforeDismiss) {
      onBeforeDismiss()
    }

    onDismiss?.()
  }

  return (
    <StyledModalContainer $minWidth="320px">
      <ModalHeader style={{ border: 'none', height: '80px', background: 'rgba(255, 255, 255, 0.10)' }}>
        <Flex alignItems="center" style={{ flex: 1 }}>
          <Box mr="8px">
            {/* <ProfileAvatar src={profile?.nft?.image?.thumbnail} height={74} width={74} fill="#ffffff99" /> */}
            <img alt="avt" src="/images/leaderboard/avt.svg" />
          </Box>
          <Box>
            {profile?.username && <Heading>{profile?.username}</Heading>}
            <ExternalLink
              style={{
                textDecoration: 'none',
              }}
              href={getBlockExploreLink(address, 'address')}
            >
              {truncateHash(address)}
            </ExternalLink>
          </Box>
        </Flex>
        <StyledIconButton variant="text" onClick={handleDismiss} aria-label="Close the dialog">
          <img src="/images/leaderboard/close.svg" alt="link" />
        </StyledIconButton>
      </ModalHeader>
      {result === null ? (
        <Text p="32px" textAlign="center" fontWeight="bold">
          {t('No results found.')}
        </Text>
      ) : (
        <Box maxHeight={['500px', null, null, null, null, null, 'none']} overflowY="auto">
          <StyledFlex p=" 16px 24px" borderBottom="1px solid rgba(255, 255, 255, 0.10) " justifyContent="space-between">
            <Box>
              <Text
                style={{ display: 'flex' }}
                as="h6"
                fontSize="14px"
                color="rgba(255, 255, 255, 0.60)"
                fontWeight="400"
                mb="8px"
              >
                {t('PnL')}{' '}
                <InfoTooltip
                  className="tooltip"
                  iconColor="rgba(255,255,255,0.6)"
                  ml="4px"
                  text="Including claimed winnings, losses, and refund amounts. Unclaimed winnings are not included."
                />
              </Text>
              {isLoading ? (
                <Skeleton />
              ) : (
                <NetWinningsView
                  token={token}
                  // amount={result?.netBNB}
                  // textPrefix={result?.netBNB > 0 ? '+' : ''}
                  // textColor={result?.netBNB > 0 ? 'success' : 'failure'}

                  amount={result?.netETH}
                  textPrefix={result?.netETH > 0 ? '+' : ''}
                  textColor={result?.netETH > 0 ? 'success' : 'failure'}
                  fontSize="14px"
                  fontWeight="700"
                  subFontSize="12px"
                  subFontWeight="400"
                  color="white"
                  // alignItems="center"
                  subFontColor="rgba(255, 255, 255, 0.60)"
                  flexDirection="column"
                  alignItems="flex-start"
                />
              )}
            </Box>
            <Box>
              <Text as="h6" fontSize="14px" color="rgba(255, 255, 255, 0.60)" fontWeight="400" mb="8px">
                {t('Win Rate')}
              </Text>
              {isLoading ? (
                <Skeleton />
              ) : (
                <Text fontSize="14px" fontWeight="700">
                  {/* {`${result?.winRate?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2})}%`} */}
                  {`${parseFloat(
                    result?.winRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                  ).toFixed(2)}%`}
                </Text>
              )}
            </Box>
            <Box>
              <Text as="h6" fontSize="14px" color="rgba(255, 255, 255, 0.60)" fontWeight="400" mb="8px">
                {t('Rounds Won')}
              </Text>
              {isLoading ? (
                <Skeleton />
              ) : (
                <Text fontSize="14px" fontWeight="700">
                  {result?.totalBetsClaimed?.toLocaleString()}
                </Text>
              )}
            </Box>
            <Box>
              <Text as="h6" fontSize="14px" color="rgba(255, 255, 255, 0.60)" fontWeight="400" mb="8px">
                {t('Rounds Played')}
              </Text>
              {isLoading ? (
                <Skeleton />
              ) : (
                <Text fontSize="14px" fontWeight="700">
                  {result?.totalBets?.toLocaleString()}
                </Text>
              )}
            </Box>
          </StyledFlex>
          {isDesktop ? (
            <DesktopBetsTable token={token} api={api} account={address} />
          ) : (
            <MobileBetsTable token={token} api={api} account={address} />
          )}
        </Box>
      )}
    </StyledModalContainer>
  )
}

export default WalletStatsModal
